import React, { useState, useEffect, FC } from "react";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import QRCode from "qrcode-generator";
import { Icon } from "../../components/Component";
import dayjs from "dayjs";
import { Button } from "reactstrap";
import { Modal } from "antd";
import { convertNumberToWords } from "../../utils/Utils";

declare module "jspdf" {
  interface jsPDF {
    autoTable: (options: any) => jsPDF;
  }
}
interface BuyerDetails {
  name: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  pincode: string;
  country: string;
  gstin: string;
  pan: string;
  cin: string;
}

interface POLines {
  sno: number;
  itemDescription: string;
  hsnSac: string;
  qty?: number;
  rate: number;
  cgstPercentage: number;
  cgstAmount: number;
  sgstPercentage: number;
  sgstAmount: number;
  taxableAmount: number;
  uom: string;
}

interface POTotals {
  taxableAmount: string;
  cgst: string;
  sgst: string;
  total: string;
}

interface QData {
  invoiceNumber: string;
  invoiceDate: string;
  terms: string;
  dueDate: string;
  expectedShipmentDate: string;
  placeofSupply: string;
  customSONumber: string;
  ewayBillNumber: string;
  vehicleRegNo: string;
  transporterName: string;
  orderNumber: string;
  driverNumber: string;
  orderDate: string;
  creditDays: number;
  deliveryAddressString: string;
  lines: POLines[];
  totals: POTotals;
}

const PdfGenerator: FC<any> = ({
  orgDetails1,
  orgDetails2,
  HeaderDetails,
  buyerOrgLogo,
  hasIGST,
  TermsAndConditions,
  getPdfUrl,
  eInvoiceData,
}: any) => {
  const additionalChargesArray = HeaderDetails?.additionalCharges || [];
  const [pdfUrl, setPdfUrl] = useState<any>(null);
  const [sm, setSm] = useState(false);
  const [mobileView, setMobileView] = useState(false);

  const viewChange = () => {
    if (window.innerWidth < 990) {
      setMobileView(true);
    } else {
      setMobileView(false);
      setSm(false);
    }
  };

  useEffect(() => {
    viewChange();
    window.addEventListener("load", viewChange);
    window.addEventListener("resize", viewChange);
    const headerClick = () => setSm(false);
    document
      .getElementsByClassName("nk-header")[0]
      ?.addEventListener("click", headerClick);

    return () => {
      window.removeEventListener("resize", viewChange);
      window.removeEventListener("load", viewChange);
      document
        .getElementsByClassName("nk-header")[0]
        ?.removeEventListener("click", headerClick);
    };
  }, []);
  const fetchImageAsBase64 = async (url: string): Promise<any> => {
    try {
      const response = await fetch(`${url}`);
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          if (reader.result) {
            console.log(
              "Image successfully fetched and converted to Base64:",
              reader.result
            ); // Debugging line
            resolve(reader.result as string);
          } else {
            reject(new Error("Failed to read the image data"));
          }
        };
        reader.onerror = () => null;
        // reject(new Error("Error reading the image data"));
        reader.readAsDataURL(blob);
      });
    } catch (error) {
      console.error("Error fetching the image:", error);
      return null;
      // throw new Error("Failed to fetch the image.");
    }
  };

  const drawBorderedBox = (doc: jsPDF) => {
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    // Define the margins for the box (e.g., 10px margins)
    const margin = 5;
    const boxX = margin;
    const boxY = 20; // Start after the header
    const boxWidth = pageWidth - 2 * margin;
    const boxHeight = pageHeight - 41; // Leave space for the footer at the bottom

    // Draw the border box
    doc.setDrawColor(157, 157, 157);
    doc.setLineWidth(0.2);
    doc.rect(boxX, boxY, boxWidth, boxHeight);
  };
  const generatePDF = async (download?: boolean) => {
    const doc = new jsPDF();

    //const leftLogo = await fetchImageAsBase64('http://localhost:3000/static/media/logo-dark2x.8758ccf4e7a33281ce73.png');

    const addImageWithMaxHeight = async (
      doc: any,
      imageData: string,
      x: number,
      y: number,
      maxHeight: number
    ) => {
      return new Promise<void>((resolve, reject) => {
        const img = new Image();
        img.src = imageData;

        img.onload = function () {
          const aspectRatio = img.width / img.height;

          // Ensure that the image height is capped at maxHeight, adjusting width accordingly
          const height = Math.min(img.height, maxHeight); // Restrict height to maxHeight
          const width = height * aspectRatio; // Adjust width based on aspect ratio

          doc.addImage(img.src, "PNG", x, y, width, height); // Add image to the PDF
          resolve();
        };

        img.onerror = function (error) {
          console.error("Image failed to load", error);
          // reject(new Error("Image load failed"));
          return "";
        };
      });
    };

    const addHeader = async (doc: jsPDF, leftLogo: string | null) => {
      if (leftLogo) {
        // If the image fetch was successful, add the image
        await addImageWithMaxHeight(doc, leftLogo, 10, 5, 10); // Await to ensure it finishes before continuing
      } else {
        // If the image fetch failed, print fallback text
        doc.setFontSize(20);
        doc.text("", 10, 20); // Fallback text in case of image fetch failure
      }
    };

    const breakLongText=(text: any, maxLineLength = 30) =>{
      if (!text) return " ";
      return text.match(new RegExp(`.{1,${maxLineLength}}`, "g")).join("\n");
    }


    const addFooter = async (
      doc: jsPDF,
      pageNum: number,
      totalPages: number
    ) => {
      const pageHeight = doc.internal.pageSize.getHeight();
      const pageWidth = doc.internal.pageSize.getWidth();
      doc.setFontSize(7);

      const line1 = `${orgDetails1.name}`;
      const line2 = `${[
        orgDetails1.addressLine1,
        orgDetails1.addressLine2,
        orgDetails1.city,
        orgDetails1.pincode,
        orgDetails1.state,
        orgDetails1.country,
      ]
        ?.filter(Boolean)
        ?.join(", ")}`;
      const line3 = `CIN: ${orgDetails1.cin} `;
      const line4 = `GSTIN: ${orgDetails1.gstin} | PAN: ${orgDetails1.pan}`;

      const lastLineY = pageHeight - 7;
      const lineSpacing = 3;
      const startY = lastLineY - 3 * lineSpacing;

      // Add footer details centered
      doc.text(line1, pageWidth / 2, startY, { align: "center" });
      doc.text(line2, pageWidth / 2, startY + lineSpacing, { align: "center" });
      doc.text(line3, pageWidth / 2, startY + 2 * lineSpacing, {
        align: "center",
      });
      doc.text(line4, pageWidth / 2, startY + 3 * lineSpacing, {
        align: "center",
      });

      // Add page number
      const pageTextY = startY - 8;
      const pageText = `Page ${pageNum} of ${totalPages}`;
      doc.text(pageText, pageWidth - 20, pageHeight - 10, { align: "right" });

      // Add any additional footer content (e.g., terms)
      doc.text(" ", 10, pageTextY);
    };
    const addGeneralDetailsSection = async (
      doc: jsPDF,
      orgDetails1: BuyerDetails,
      HeaderDetails: QData,
      orgDetails2: any
    ) => {
      const sectionY = 16; // Starting Y position for the General Details Section
      const pageWidth = doc.internal.pageSize.getWidth();

      // Set font for heading and add text for General Details Section
      doc.setFontSize(11);
      doc.setFont("helvetica", "bold"); // Bold for the heading
      doc.setTextColor(0, 0, 0); // Black text color

      let currentY = sectionY + 10; // Keep track of the current Y position

      // Add buyer details
      if (orgDetails1) {
        const lineHeight = 4;
        doc.text(orgDetails1.name || "", 10, currentY);
        currentY += 5; // Move Y position for the next line

        doc.setFontSize(8);
        doc.setFont("helvetica", "normal");
        const address1 = breakLongText(orgDetails1.addressLine1 || "");
        doc.text(address1, 10, currentY);
        currentY += address1.split("\n").length * lineHeight;

        const address2 = breakLongText(orgDetails1.addressLine2 || "");
        doc.text(address2, 10, currentY);
        currentY += address2.split("\n").length * lineHeight;
        doc.text(
          `${orgDetails1.city || ""}, ${orgDetails1.state || ""}, ${orgDetails1.country || ""},${
            orgDetails1.pincode || ""
          }`,
          10,
          currentY
        );

        currentY += 5;
        const gstin = breakLongText(`GSTIN: ${orgDetails1.gstin || ""}`);
        doc.text(gstin, 10, currentY);
        currentY += gstin.split("\n").length * lineHeight;

        const pan = breakLongText(`PAN: ${orgDetails1.pan || ""}`);
        doc.text(pan, 10, currentY);
        currentY += pan.split("\n").length * lineHeight;

        const cin = breakLongText(`CIN: ${orgDetails1.cin || ""}`);
        doc.text(cin, 10, currentY);
        currentY += cin.split("\n").length * lineHeight;

        currentY += 5; // Add extra space after buyer details
      }

      // Add the "Quotation" text on the right, aligned at the top
      doc.setFontSize(20);
      doc.setFont("helvetica", "bold");
      const purchaseOrderText = "INVOICE";

      // Calculate X position for the text to be right-aligned
      const textWidth = doc.getTextWidth(purchaseOrderText); // Get the width of the text
      const rightX = pageWidth - textWidth - 10; // Position the text with 10px padding from the right edge
      doc.setTextColor(20, 20, 20); // Black text color
      doc.text(purchaseOrderText, rightX, sectionY + 35); // Align the text at the top-right

      // Draw a horizontal line to separate sections
      doc.setDrawColor(150, 150, 150); // Gray color for the line
      doc.line(5, currentY, pageWidth - 5, currentY);
      currentY += 5;

      // Add Quotation details on the left and right sections
      const sectionWidth = (pageWidth - 10) / 2; // Subtracting the left and right margins
      let leftTableHeight = currentY;
      let rightTableHeight = currentY;

      // Capture the current Y position before the tables are drawn to draw the vertical line later
      let tableStartY = currentY;

      // Left table (PO details)
      doc.autoTable({
        startY: currentY,
        theme: "plain",
        showHead: "never",
        body: [
          ["Invoice Serial#", breakLongText(HeaderDetails.invoiceNumber || "N/A")],
          ["Invoice Date ", HeaderDetails.invoiceDate || "N/A"],
          ["Payment Due Date", HeaderDetails.dueDate || "N/A"],
          ["Payment Terms", HeaderDetails.terms || "N/A"],
          ["Credit Days", HeaderDetails?.creditDays?.toString() || "N/A"],
          // ["Customer SO#", HeaderDetails.customSONumber|| "N/A"],
          ["E-Way Bill#", HeaderDetails.ewayBillNumber || "N/A"],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        margin: { left: 10 },
        didDrawCell: (data: any) => {
          leftTableHeight = Math.max(
            leftTableHeight,
            data.cell.y + data.cell.height
          ); // Track maximum Y position for the left table
        },
      });

      // Right table (Supplier details)
      doc.autoTable({
        startY: currentY,
        margin: { left: sectionWidth + 10 },
        theme: "plain",
        showHead: "never",
        body: [
          ["Place of Supply", HeaderDetails?.placeofSupply || "N/A"],
          ["Contact Person (site)", HeaderDetails?.transporterName || "N/A"],
          ["Mobile Number", HeaderDetails?.driverNumber || "N/A"],
          ["Vehicle Regn#", HeaderDetails?.vehicleRegNo || "N/A"],
          ["Order #", HeaderDetails?.orderNumber || "N/A"],
          ["Order Date", HeaderDetails?.orderDate || "N/A"],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 40 },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        didDrawCell: (data: any) => {
          rightTableHeight = Math.max(
            rightTableHeight,
            data.cell.y + data.cell.height
          ); // Track maximum Y position for the right table
        },
      });

      let maxHeight = Math.max(leftTableHeight, rightTableHeight); // Calculate the maximum height

      // Draw the vertical line separator between the left and right tables without any gap on top and bottom
      doc.setDrawColor(150, 150, 150); // Gray color for the line
      doc.line(sectionWidth + 5, tableStartY, sectionWidth + 5, maxHeight + 5); // Ensure the line extends beyond the bottom by 5 units

      const verticalLineX = sectionWidth + 5; // Position of the vertical line
      // Add Supplier Details section after the tables
      currentY = maxHeight + 10; // Move below the tables
      doc.setFillColor(200, 200, 200);
      doc.rect(5, currentY, pageWidth - 10, 6, "F"); // Title background
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.setFont("helvetica", "bold");
      doc.text("Name and Address of the Customer (Bill To)", 10, currentY + 4);
      doc.text("Shipping Address of the Customer (Ship To)", 110, currentY + 4);
      currentY += 12; // Move down after the title

      const availableWidth = sectionWidth - 20; // Section width minus left margin (10px) and some buffer (10px)
      const addressLine1Splits = doc.splitTextToSize(
        orgDetails2.addressLine1 || "",
        availableWidth
      );
      const addressLine2Splits = doc.splitTextToSize(
        orgDetails2.addressLine2 || "",
        availableWidth
      );
      const city = orgDetails2?.city || "";
      const state = orgDetails2?.state || "";
      const country = orgDetails2?.country || "";
      const pincode = orgDetails2?.pincode || "";
      const countryAndPincode = [city,state,country, pincode].filter(Boolean).join(", "); 

      tableStartY = currentY;
      leftTableHeight = currentY;
      rightTableHeight = currentY;
      doc.autoTable({
        startY: currentY,
        theme: "plain",
        showHead: "never",
        body: [
          [orgDetails2.name || ""],
          [addressLine1Splits], 
          [addressLine2Splits], 
          [countryAndPincode || ""],
          ["CIN: " + orgDetails2.cin || ""],
          ["GSTIN:" + orgDetails2.gstin || ""],
          ["PAN: " + orgDetails2.pan || ""],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: availableWidth },
          // 1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        margin: { left: 10 },
        didDrawCell: (data: any) => {
          leftTableHeight = Math.max(
            leftTableHeight,
            data.cell.y + data.cell.height
          ); // Track maximum Y position for the left table
        },
      });

      // Right table (Supplier details)
      doc.autoTable({
        startY: currentY,
        margin: { left: sectionWidth + 10 },
        theme: "plain",
        showHead: "never",
        body: [
          [HeaderDetails.deliveryAddressString || ""],
          ["Contact Person: " + orgDetails2.contactPersonName || ""],
          ["Mobile Number: " + orgDetails2.mobileNumber || ""],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 80 },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        didDrawCell: (data: any) => {
          rightTableHeight = Math.max(
            rightTableHeight,
            data.cell.y + data.cell.height
          ); // Track maximum Y position for the right table
        },
      });
      maxHeight = Math.max(leftTableHeight, rightTableHeight); // Calculate the maximum height

      // Draw the vertical line separator between the left and right tables without any gap on top and bottom
      doc.setDrawColor(150, 150, 150); // Gray color for the line
      doc.line(verticalLineX, tableStartY, verticalLineX, leftTableHeight); // Ensure the line extends beyond the bottom by 5 units

      return maxHeight + 10; // Return the final Y position to calculate the next section start point
      // return currentY; // Return the final Y position to calculate the next section start point
    };

    const addTermsAndConditions = (doc: jsPDF) => {
      // Set the font for Terms and Conditions
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("Terms and Conditions", 10, 30);

      doc.setFontSize(10);
      doc.setFont("helvetica", "normal");

      const termsAndConditionsContent: any = TermsAndConditions || "";

      const maxLineWidth = 180; // Set the width to fit within the page margins
      const termsAndConditions = termsAndConditionsContent.split("\n");

      let currentY = 40;
      const lineHeight = 4.5;
      const pageHeight = doc.internal.pageSize.getHeight(); // Get the page height

      termsAndConditions.forEach((point: any) => {
        const wrappedText = doc.splitTextToSize(point, maxLineWidth);

        // Check if the current Y position exceeds the page height
        if (currentY + wrappedText.length * lineHeight > pageHeight - 30) {
          doc.addPage(); // Add a new page
          currentY = 30; // Reset Y position for the new page
        }

        doc.text(wrappedText, 10, currentY); // Print the text
        currentY += wrappedText.length * lineHeight; // Adjust Y position
      });
    };

    const addTable = (doc: any, HeaderDetails: any, currentY: any) => {
      const mainTitles: any = [
        { content: "Sno", rowSpan: 2 },
        { content: "Item & Description", rowSpan: 2 },
        { content: "HSN/SAC", rowSpan: 2 },
        { content: "QTY", rowSpan: 2 },
        { content: "Rate", rowSpan: 2 },
      ];
      if (hasIGST) {
        mainTitles.push({ content: "IGST", colSpan: 2 });
        mainTitles.push({ content: "Line Total", rowSpan: 2 });
      } else {
        mainTitles.push({ content: "CGST", colSpan: 2 });
        mainTitles.push({ content: "SGST", colSpan: 2 });
        mainTitles.push({ content: "Line Total", rowSpan: 2 });
      }
      const subTitles = [{ content: "%" }, { content: "Amount" }];
      if (!hasIGST) {
        subTitles.push({ content: "%" }, { content: "Amount" });
      }
      const head = [mainTitles, subTitles];

      const rows = HeaderDetails?.lines?.map((line: any, index: any) => {
        const row = [
          (index + 1).toString(),
          line.itemDescription,
          line.hsnSac,
          line.qty || "",
          line.rate,
        ];

        if (hasIGST) {
          row.push(line.igstPercentage?.toFixed(2), line.igstAmount);
        } else {
          row.push(
            line.cgstPercentage?.toFixed(2),
            line.cgstAmount,
            line.sgstPercentage?.toFixed(2),
            line.sgstAmount
          );
        }

        row.push(line.taxableAmount);
        return row;
      });

      let footer: any = [];

      if (hasIGST) {
        const additionalChargesRows = additionalChargesArray.map(
          (charge: any) => [
            "",
            "",
            "",
            "",
            "",
            { content: `${charge.name} :`, colSpan: 2 },
            Number(charge.price).toLocaleString(),
          ]
        );
        footer = [
          [
            "Total In Words",
            "",
            "",
            "",
            "",
            { content: "Taxable Amount:", colSpan: 2 },
            HeaderDetails?.totals?.taxableAmount,
          ],
          [
            {
              content:
                "Indian Rupee " +
                convertNumberToWords(
                  HeaderDetails?.totals?.total?.replace(/,/g, "")
                ),
              colSpan: 4,
            },
            "",
            { content: "IGST:", colSpan: 2 },
            HeaderDetails?.totals?.igst,
          ],
          ...additionalChargesRows,
          [
            "",
            "",
            "",
            "",
            "",
            { content: "Total:", colSpan: 2 },
            HeaderDetails?.totals?.total,
          ],
          [
            { content: "Bank Details", colSpan: 1, style: { halign: "left" } },
            "",
            "",
            "",
            "",
            "",
          ],
          [
            {
              content: "Account Name : " + orgDetails1?.accountHolderName,
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
          [
            {
              content: "Account Number : " + orgDetails1?.accountNumber,
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
          [
            {
              content: "Bank Name : " + orgDetails1?.bankName,
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            { content: "Authorised Signature              ", colSpan: 3, halign: "left" },
          ],
          [
            {
              content: "IFSC Code: " + orgDetails1?.ifscCode,
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
        ];
      } else {
        const additionalChargesRows = additionalChargesArray.map(
          (charge: any) => [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: `${charge.name} :`, colSpan: 2 },
            Number(charge.price).toLocaleString(),
          ]
        );
        footer = [
          [
            {
              content: "Total In Words",
              colSpan: 2,
              style: { halign: "left" },
            },
            "",
            "",
            "",
            "",
            { content: "Taxable Amount:", colSpan: 3 },
            HeaderDetails?.totals?.taxableAmount,
          ],
          [
            {
              content:
                "Indian Rupee " +
                convertNumberToWords(
                  HeaderDetails?.totals?.total?.replace(/,/g, "")
                ),
              colSpan: 5,
            },
            // "",
            // "",
            // "",
            // "",
            // "",
            "",
            "",
            { content: "CGST :", colSpan: 2 },
            HeaderDetails?.totals?.cgst,
          ],
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "SGST :", colSpan: 2 },
            HeaderDetails?.totals?.sgst,
          ],
          ...additionalChargesRows,
          [
            "",
            "",
            "",
            "",
            "",
            "",
            "",
            { content: "Total:", colSpan: 2 },
            HeaderDetails?.totals?.total,
          ],
          [
            {
              content: "Bank Details ",
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
          [
            {
              content: "Account Name : " + orgDetails1?.accountHolderName,
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
          [
            {
              content: "Bank Name : " + orgDetails1?.bankName,
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
          [
            {
              content: "Account Number : " + orgDetails1?.accountNumber,
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            {
              content: "Authorised Signature              ",
              colSpan: 5,
              styles: { halign: "right" },
            },
          ],
          [
            {
              content: "IFSC Code : " + orgDetails1?.ifscCode,
              colSpan: 4,
              style: { fontStyle: "normal" },
            },
            "",
            "",
            "",
            "",
          ],
        ];
      }

      doc.autoTable({
        head,
        body: rows,
        foot: footer,
        startY: currentY,
        pageBreak: "auto",
        columnStyles: {
          4: { halign: "right" }, // Rate column width, right-aligned
          5: { halign: "right" }, // CGST % column width, right-aligned
          6: { halign: "right" }, // CGST Amt column width, right-aligned
          7: { halign: "right" }, // SGST % column width, right-aligned
          8: { halign: "right" }, // SGST Amt column width, right-aligned
          9: { halign: "right" }, // Taxable Amount column width, right-aligned
        },

        didParseCell: (data: any) => {
          // Align the text of the last column ('Taxable Amount', 'CGST (5%)', 'SGST (5%)', 'Total')

          if (data.section === "foot" && data.column.index === 7) {
            data.cell.styles.halign = "right"; // Manually set right alignment
          }
          if (data.section === "foot" && data.column.index === 9) {
            data.cell.styles.halign = "left"; // Manually set right alignment
          }
          // Make 'Total' row bold
          if (
            (data.section === "foot" &&
              (data.row.index >= 3 ||
                data.row.index < 3 + additionalChargesArray.length)) ||
            (data.section === "foot" &&
              (data.row.index >= 2 ||
                data.row.index < 2 + additionalChargesArray.length) &&
              hasIGST)
          ) {
            data.cell.styles.fontStyle = "bold"; // Set bold style for the "Total" row
            data.cell.styles.halign = "right";
          }

          if (data.section === "foot" && data.column.index == 0) {
            data.cell.styles.halign = "left"; // Manually set right alignment
            data.cell.styles.fontStyle = "bold";
            if (data.row.index === 1) data.cell.styles.fontStyle = "bolditalic";
          } else if (data.section === "foot") {
            data.cell.styles.fontStyle = "normal"; // Ensure other rows are not bold
            data.cell.styles.halign = "right";
          }

          if (
            data.section === "foot" &&
            data.column.index > 3 &&
            data.row.index >= (hasIGST ? 2 : 3)
          ) {
            data.cell.styles.fontStyle = "bold"; // setting bold from row starts from additional charges
            data.cell.styles.halign = "right";
          }
        },
        didDrawCell: (data: any) => {
          const { cell } = data;

          doc.setDrawColor(150, 150, 150);

          if (
            data.section === "foot" &&
            (data.column.index === 6 ||
              data.column.index === 7 ||
              data.column.index === 8 ||
              data.column.index === 9 ||
              (data.column.index === 5 && hasIGST))
          ) {
            if (data.column.index === (hasIGST ? 5 : 6)) {
              doc.line(cell.x, cell.y, cell.x, cell.y + cell.height); // Left vertical line for the label column
            }

            if (data.row.index === 3 + additionalChargesArray?.length) {
              doc.line(
                cell.x,
                cell.y + cell.height,
                cell.x + cell.width,
                cell.y + cell.height
              ); // Bottom horizontal line
            }
            if (
              data.row.index ===
              additionalChargesArray?.length + (hasIGST ? 7 : 8)
            ) {
              doc.line(
                cell.x,
                cell.y + cell.height,
                cell.x + cell.width,
                cell.y + cell.height
              ); // Bottom horizontal line
            }
          }
        },
        headStyles: {
          fontSize: 9,
          fillColor: [227, 227, 227],
          textColor: [31, 31, 31],
          valign: "middle",
        },
        footStyles: {
          fontSize: 9,
          textColor: [31, 31, 31],
          lineWidth: 0,
        },
        styles: {
          fillColor: [255, 255, 255],
          fontSize: 8,
          lineWidth: 0.2,
          cellPadding: 1.5,
          overflow: "linebreak",
        },

        theme: "grid",
        margin: { top: 20, bottom: 20, left: 5.1, right: 5.1 },
        showFoot: "lastPage",
      });
    };

    const addIrnQr = async (doc: jsPDF, qrCodeData: any) => {
      const sectionY = 16;
      const pageWidth = doc.internal.pageSize.getWidth();

      doc.setFontSize(11);
      doc.setFont("helvetica", "bold"); // Bold for the heading
      doc.setTextColor(0, 0, 0); // Black text color

      let currentY = sectionY + 10; // Keep track of the current Y position
      const sectionWidth = pageWidth / 2; // Subtracting the left and right margins
      let rightTableHeight = currentY;

      // Capture the current Y position before the tables are drawn to draw the vertical line later

      doc.addImage(qrCodeData, "PNG", 10, 25, 35, 35); // Centered below header
      doc.autoTable({
        startY: currentY + 10,
        margin: { left: sectionWidth - 50 },
        theme: "plain",
        showHead: "never",
        body: [
          ["IRN :", eInvoiceData?.Irn || "N/A"],
          ["Ack No. :", eInvoiceData?.AckNo || "N/A"],
          [
            "Ack Date :",
            dayjs(eInvoiceData?.AckDat)?.format("DD-MMM-YYYY hh:mm A") || "N/A",
          ],
        ],
        styles: {
          fontSize: 8,
          cellPadding: { bottom: 1 },
          overflow: "linebreak",
        },
        columnStyles: {
          0: { cellWidth: 30 },
          1: { cellWidth: "auto", halign: "left", fontStyle: "bold" },
        },
        didDrawCell: (data: any) => {
          rightTableHeight = Math.max(
            rightTableHeight,
            data.cell.y + data.cell.height
          ); // Track maximum Y position for the right table
        },
      });
    };

    const leftLogo = buyerOrgLogo
      ? await fetchImageAsBase64(buyerOrgLogo)
      : null;

    const currentY = await addGeneralDetailsSection(
      doc,
      orgDetails1,
      HeaderDetails,
      orgDetails2
    );

    addTable(doc, HeaderDetails, currentY);
    const applyHeaderAndFooter = async () => {
      const totalPages = doc.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        await addHeader(doc, leftLogo);
        await addFooter(doc, i, totalPages);
        drawBorderedBox(doc); // Ensure borders are added to each page if needed
      }
    };
    if (eInvoiceData?.SignedQRCode) {
      doc.addPage();
      const base64Payload = eInvoiceData?.SignedQRCode?.split(".")?.[1];
      const payload = JSON.parse(atob(base64Payload));
      const qrData = JSON.stringify(payload.data); // Converting the data field into a string for QR code generation

      // Generate QR Code using qrcode-generator
      const qr = QRCode(0, "L"); // Error correction level L (low)
      qr.addData(qrData);
      qr.make();

      // Retrieve the QR code as a base64 image URL
      const qrCodeData = qr.createDataURL(4); // 4 is the scale factor for higher resolution

      const pageWidth = doc.internal.pageSize.width;
      doc.setDrawColor(150, 150, 150); // Gray color for the line
      doc.line(5, 80, pageWidth - 5, 80);
      // doc.addImage(qrCodeData, "PNG", 10, 25, 35, 35); // Centered below header

      await addIrnQr(doc, qrCodeData);
    }

    if (TermsAndConditions && TermsAndConditions.trim() !== "") {
      doc.addPage();
      addTermsAndConditions(doc);
    }
    await applyHeaderAndFooter();

    if (download) {
      doc.save(HeaderDetails?.invoiceNumber || "invoice" + ".pdf");
      return {};
    }

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    // setPdfUrl(pdfUrl); // Set the Blob URL to display the PDF inline
    return { pdfUrl, pdfBlob };
  };

  (async () => {
    try {
      const { pdfBlob } = await generatePDF();
      getPdfUrl(pdfBlob);
    } catch (error) {
      console.log(error);
    }
  })();

  const downloadPDF = async () => {
    await generatePDF(true);
  };

  return (
    <>
      <Button
        type="button"
        color=""
        onClick={async () => {
          const { pdfUrl } = await generatePDF();
          setPdfUrl(pdfUrl);
        }}
      >
        <Icon name="file-pdf" /> PDF
      </Button>
      {pdfUrl && (
        <Modal
          open={pdfUrl !== null}
          onCancel={() => setPdfUrl(null)}
          width={"90vw"}
          footer={null}
          title={
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span>Invoice</span>
              <span
                style={{ cursor: "pointer" }}
                onClick={async () => {
                  await downloadPDF();
                }}
              >
                Download
                <Icon name="download" className="me-4" />
              </span>
            </div>
          }
        >
          <iframe
            src={pdfUrl + "#toolbar=0"}
            style={{
              width: "100%",
              height: "80vh",
            }}
            title="PDF Viewer"
          ></iframe>
        </Modal>
      )}
    </>
  );
};

export default PdfGenerator;
